<script setup>
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import MultiSelect from '../ui/MultiSelect.vue';
import { ElButton, ElDialog, ElMessage } from 'element-plus';
import axios, { HttpStatusCode } from 'axios';

const props = defineProps({
    product: Object,
    toolReference: String,
    mappings: Array,
    service: String,
});

const queryClient = useQueryClient();

const isIntellifloService = computed(() => props.service === 'INTELLIFLO');
const fetchAll = ref(false);
const showDialog = ref(false);

const productsQueryKey = computed(() => (
    isIntellifloService.value
        ? ['pricing-products', props.service, props.toolReference, props.product?.providerName, fetchAll.value]
        : ['pricing-products', props.service, props.toolReference]
));

const productsQueryEnabled = computed(() => (
    isIntellifloService.value ? showDialog.value : true
));

function getProductsUrl() {
    const baseUrl = `api/Configuration/ListPricingProducts/${props.service}`;
    if (isIntellifloService.value) {
        return `${baseUrl}/${encodeURIComponent(props.toolReference || '')}/${encodeURIComponent(props.product?.providerName || '')}?fetchAll=${fetchAll.value}`;
    }
    return `${baseUrl}/${encodeURIComponent(props.toolReference || '')}`;
}

const {
    data: products,
    isFetching: isFetchingProducts,
} = useQuery({
    queryKey: productsQueryKey,
    enabled: productsQueryEnabled,
    queryFn: () => axios(getProductsUrl()).then(res => res.data),
});


const options = computed(() => {
    const currentProviderKey = String(props.product?.providerReference || '').trim().toLowerCase();
    const mappedProducts = isIntellifloService.value ? new Set() : new Set(
        (props.mappings || [])
            .filter(m => String(m.apiProductId || '').trim().toLowerCase() !== currentProviderKey)
            .flatMap(m => m.externalIds || [])
            .map(id => String(id))
    );
    const source = products.value || [];
    return source.map(product => {
        const key = String(product.productId);
        return {
            key,
            label: product.productId + " - " + product.productName,
            disabled: mappedProducts.has(key),
        };
    })
})

const mapping = computed(() => props.mappings?.find(m => m.apiProductId == props.product.providerReference));
const value = ref(mapping?.value?.externalIds || []);
const mapCount = computed(() => mapping.value?.externalIds?.length || 0);

watch(() => mapping.value, (newValue) => {
    const next = newValue?.externalIds || [];
    value.value = isIntellifloService.value ? next.slice(0, 1) : next;
})

// Preserve selected value before fetchAll refetch
const savedValue = ref([]);

watch(showDialog, (isOpen) => {
    if (isOpen && isIntellifloService.value) {
        fetchAll.value = false;
    }
});

const { mutate, isLoading: isSaving } = useMutation({
    mutationFn: async (request) => {
        try {
            const response = await axios.post('api/Configuration/SaveProductMapping', JSON.stringify(request), {
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === HttpStatusCode.Ok) {
                return response.data;
            } else {
                throw new Error(response.statusText);
            }
        } catch (error) {
            throw new Error(error.message);
        }
    },
    onSuccess: () => {
        ElMessage.success('Mapping saved.');
        queryClient.invalidateQueries({ queryKey: ['tool-mappings', props.service, props.toolReference] });
        showDialog.value = false;
    },
    onError: () => {
        ElMessage.error('Mapping could not be saved');
    }
});

function saveMappings() {
    mutate({
        serviceType: props.service,
        apiProductId: props.product.providerReference,
        toolReference: props.toolReference,
        externalIds: isIntellifloService.value ? value.value.slice(0, 1) : value.value,
    });
}

const leftTitle = computed(() => {
    if (isIntellifloService.value) {
        return 'Intelliflo products';
    }
    if (props.service === 'IPIPELINE') {
        return 'iPipeline products';
    }
    return 'Products';
});

const serviceLabel = computed(() => {
    if (isIntellifloService.value) {
        return 'Intelliflo';
    }
    if (props.service === 'IPIPELINE') {
        return 'iPipeline';
    }
    return 'the integration';
});

function handleAvailableFilterChange() {
    // For Intelliflo, filtering is handled client-side by the MultiSelect component itself.
    // No server-side filter needed.
}

function handleFetchAll() {
    savedValue.value = [...value.value];
    fetchAll.value = true;
}

// Restore selected value once fetchAll products arrive
watch(products, (newProducts) => {
    if (fetchAll.value && savedValue.value.length && newProducts?.length) {
        value.value = [...savedValue.value];
        savedValue.value = [];
    }
});

function handleMaxReached() {
    if (!isIntellifloService.value) {
        return;
    }
    ElMessage.warning('Only one product can be mapped for Intelliflo.');
}

const titles = computed(() => ({ left: leftTitle.value, right: props.product.providerName }));
</script>
<template>
    <div class="row">
        <div class="title">{{ product.providerName }}</div>
        <div class="subtitle">
            <span>{{ product.providerReference }}</span>
            <span class="mapping" @click="showDialog = true">{{ mapCount }} {{ mapCount == 1 ? 'product' : 'products' }}
                mapped</span>
        </div>
    </div>
    <ElDialog v-model="showDialog" destroy-on-close>
        <p class="tip">Select the products from {{ serviceLabel }} to be mapped to <strong>{{ product.providerName }}</strong></p>
        <span v-if="isIntellifloService && !fetchAll" class="fetch-all-link" @click="handleFetchAll">
            Fetch all products
        </span>
        <div v-if="isIntellifloService && fetchAll && isFetchingProducts" class="loading-banner">
            <span class="loading-spinner" />
            Loading all products, this may take a moment...
        </div>
        <MultiSelect
            v-model="value"
            :data="options"
            :titles="titles"
            :max-selected="isIntellifloService ? 1 : null"
            @filter-available-change="handleAvailableFilterChange"
            @max-reached="handleMaxReached"
        />
        <template #footer>
            <div class="dialog-footer">
                <ElButton type="primary" link @click="showDialog = false">Cancel</ElButton>
                <ElButton type="primary" :loading="isSaving" @click="saveMappings">Save and close</ElButton>
            </div>
        </template>
    </ElDialog>
</template>
<style scoped>
.row {
    max-width: 100%;
    padding: 0.5rem;
    border-radius: 0.5rem;
}

.title {
    font-weight: bold;
}

.subtitle {
    display: flex;
    font-size: small;
    gap: 1rem
}

.subtitle .mapping {
    color: var(--el-color-primary);
    text-decoration: underline;
    cursor: pointer;
}

.fetch-all-link {
    color: var(--el-color-primary);
    text-decoration: underline;
    cursor: pointer;
    font-size: small;
    display: inline-block;
    margin-bottom: 0.5rem;
}

.fetch-all-link:hover {
    opacity: 0.7;
}

.loading-banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 1rem;
    margin-bottom: 0.5rem;
    background: var(--el-color-primary-light-9);
    border-radius: 0.5rem;
    font-size: small;
    color: var(--el-color-primary);
}

.loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--el-color-primary-light-5);
    border-top-color: var(--el-color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    flex-shrink: 0;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.dialog-footer {
    display: inline-flex;
    gap: 2rem;
}

.dialog-footer button {
    width: unset;
}
</style>
